import { Bell, Copy } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import { Card, CardBody, IconButton, Tooltip, TooltipProvider } from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import styles from './JobDetailNotificationCallbackCard.module.css';

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  callbackConfig: any;
}

export function JobDetailNotificationCallbackCard({ callbackConfig }: Props) {
  const toast = useToast();

  if (!callbackConfig) return null;

  const callbackUrl: string = callbackConfig?.url ?? '';

  const rows: { label: string; value?: string | null; isUrl?: boolean }[] = [
    { label: 'URL', value: callbackUrl, isUrl: true },
    { label: 'Method', value: callbackConfig?.method },
    { label: 'Auth Type', value: callbackConfig?.auth_type },
  ].filter((r) => r.value != null && r.value !== '');

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <Section title="Notification Callback" description="Endpoint called on job completion." style={{ marginBottom: 0 }}>
      <Card flat>
        <CardBody>
          <div className={styles.header}>
            <Bell size={16} className={styles.icon} />
            <span className={styles.name}>{callbackConfig?.name || 'Callback'}</span>
          </div>
          {rows.length > 0 ? (
            <TooltipProvider>
              <dl className={styles.fieldList}>
                {rows.map((r) => (
                  <div key={r.label} className={styles.fieldRow}>
                    <dt className={styles.fieldLabel}>{r.label}</dt>
                    <dd className={styles.fieldValue}>
                      <span className={styles.fieldText}>{r.value}</span>
                      {r.isUrl && r.value && (
                        <Tooltip content="Copy URL" side="top">
                          <IconButton
                            aria-label="Copy callback URL"
                            variant="ghost"
                            size="sm"
                            onClick={() => void copy(r.value!)}
                          >
                            <Copy size={12} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </TooltipProvider>
          ) : (
            <p className={styles.empty}>No callback details available.</p>
          )}
        </CardBody>
      </Card>
    </Section>
  );
}
